import { useMemo, useRef, useState } from 'react';
import * as Slider from '@radix-ui/react-slider';
import { BookOpen, Columns3, Filter, Radar } from 'lucide-react';
import { ALL_MARKET_CODES, CANONICAL_SUBSEGMENTS, MARKET_NAMES, type MarketCode } from '@hc/shared';
import { PanelEmptyState } from '../panel-empty-state.js';
import { PanelFrame } from '../panel-frame.js';
import {
  useAppStore,
  useGlobalCurrency,
  usePanelEntityId,
  useSetPanelEntity,
  type PanelId,
  type TabId,
} from '../store.js';
import { useDockviewApi } from '../dockview-context.js';
import type { PanelParams, PanelComponentKey } from '../components.js';
import { useOriginationSignal } from '../../api/hooks.js';
import { useScreenerFacets } from '../../api/screener-hooks.js';
import { EntityRowContextMenu } from '../entity-row-context-menu.js';
import { ColumnsMenu } from '../columns-menu.js';
import { ColumnHeaderCell } from '../column-header-cell.js';
import {
  useReferralsColumns,
  REFERRALS_COLUMN_DEFS,
  type ReferralsColumnId,
} from '../referrals-columns.js';
import type { OriginationSignalRow } from '../../api/client.js';
import { convertFromEur, CURRENCY_SYMBOL, type GlobalCurrency } from '../../currency.js';
import {
  FilterModalShell,
  CollapsibleFilterRow,
  MultiSelectList,
  GroupedRegionList,
  summarizeSelection,
  type SelectOption,
} from '../filter-modal-kit.js';

// Referrals — a single curated feed over BOTH Corryk proprietary signals: the
// Origination Signal (attractiveness) and the Seller Readiness Score
// (gettability), shown as a side-by-side pair. An entity qualifies if it is
// top-tier on EITHER (bars fixed server-side at 75/100, not user-selectable);
// the server orders by a combined geometric-mean so "attractive AND gettable"
// leads. Filters narrow the view but never change a score. Origination Signal
// is GB-only until per-market model exports land (audit W3.1) → "—" elsewhere;
// Seller Readiness is cross-market since audit W2.1 and carries the non-GB
// legs of the union gate. DISTINCT from the region-level Corryk Opportunity
// Index (Indications).

// Markets with a non-empty ≥75 population on EITHER signal — a country whose
// best score sits below the fixed bar would only ever serve an empty feed.
//
// ALL 27 NOW CLEAR THAT BAR, so the list is the full market set.
//
// The old hardcoded six (GB, FR, CZ, NO, SK, EE) were correct when written on
// 2026-07-11 and quietly became wrong as the ingests deepened — the comment
// even said "re-add each as its owner-age / financials ingest deepens", which
// is a manual step nobody was ever going to remember. By 2026-07-21 the list
// was inverted: it EXCLUDED the four largest above-bar markets and kept one of
// the smallest.
//
//   above the ≥75 bar, measured 2026-07-21 (either signal):
//     ES 28,272 · BE 17,158 · GB 10,809 · DE 9,289 · GR 7,831 · CZ 6,760
//     FR 6,569 · IT 6,169 · PL 6,116 · NL 4,300 · NO 3,434 · RO 3,400 …
//     … smallest is PT at 76. Nothing is empty; nothing is close to empty.
//
// So the guard it implemented no longer guards anything, while hiding 21
// markets' worth of finished work. A market that ever does fall to zero shows
// an empty list, which is self-evident in the UI — it does not need an
// allowlist that rots silently between ingests.
const LIVE_COUNTRIES: readonly MarketCode[] = ALL_MARKET_CODES;

// Revenue ladder (€m) for the dual slider — index 0 = Any, last = No max.
const REV_STOPS_M = [0, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000] as const;
const REV_LAST = REV_STOPS_M.length - 1;

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
function ramp(t: number): string {
  t = clamp(t, 0, 1);
  const a = [255, 241, 229];
  const b = [168, 87, 42];
  const c = a.map((x, i) => Math.round(x + (b[i]! - x) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}
const textOn = (t: number) => (t > 0.52 ? '#fff1e5' : '#1a1410');
// Both signal chips share ONE ramp (the brand orange heat): two different hues
// made an equally-high score read as if one signal were inferior. Same hue =
// intensity-only; the column header + sub-caption ("percentile" / "readiness")
// say which signal. Each maps over its own meaningful range so "high for that
// signal" lands dark for both.

// "Why referred" — merge the Origination + Seller-Readiness rationales into ONE
// deduplicated clause list, so a reason that drove BOTH scores appears once.
// Two layers of dedup: exact-phrase (identical clauses), and the owner-age /
// succession theme (origination's "owner approaching retirement" + readiness's
// "Owner 77" are the same fact) — collapsed to the most specific (the one with
// the actual age).
// Model-provenance notes ("NO-2026Q3-orig-v1 — agent-scored overlay (CV AUC
// 0.77)") are audit trail, not a referral reason — a user asked WHY an entity
// is referred and got a version string. Detect them so the cell can render a
// human sentence instead; the provenance stays available as the cell tooltip.
function isProvenanceNote(note: string | null): boolean {
  if (!note) return false;
  return /agent-scored overlay|cv auc|orig-v\d|native model|temporal holdout/i.test(note);
}

// Human referral reason for a row whose origination note is pure provenance.
// The Signal IS the within-market percentile, so the honest human reading is
// the entity's rank on the market's deal-propensity model.
function humanModelReason(r: OriginationSignalRow): string {
  const sig = r.originationSignal;
  const where = [MARKET_NAMES[r.country as keyof typeof MARKET_NAMES] ?? r.country, r.subsegment]
    .filter(Boolean)
    .join(' ');
  if (sig == null) return `Scored by the ${where} deal-propensity model`;
  return `Ranks in the ${sig}${ordinalSuffix(sig)} percentile on ${where}'s deal-propensity model — profile matches operators that historically transacted`;
}

function combineReasons(orig: string | null, seller: string | null | undefined): string {
  const seen = new Set<string>();
  const items: Array<{ phrase: string; ageTheme: boolean }> = [];
  for (const note of [orig, seller]) {
    if (!note) continue;
    for (const raw of note.split(/[,;·]/)) {
      const phrase = raw.trim().replace(/\.\s*$/, '');
      if (!phrase) continue;
      const key = phrase.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ phrase, ageTheme: /retirement|owner\s*\d/i.test(phrase) });
    }
  }
  const ageItems = items.filter((i) => i.ageTheme);
  if (ageItems.length > 1) {
    const best = ageItems.find((i) => /\d/.test(i.phrase)) ?? ageItems[0];
    return items
      .filter((i) => !i.ageTheme || i === best)
      .map((i) => i.phrase)
      .join(' · ');
  }
  return items.map((i) => i.phrase).join(' · ');
}

function fmtMoney(eur: number, ccy: GlobalCurrency): string {
  const v = convertFromEur(eur, ccy);
  const sym = CURRENCY_SYMBOL[ccy];
  const a = Math.abs(v);
  if (a >= 1e9) return sym + (v / 1e9).toFixed(1) + 'B';
  if (a >= 1e6) return sym + (v / 1e6).toFixed(1) + 'M';
  if (a >= 1e3) return sym + (v / 1e3).toFixed(0) + 'K';
  return sym + String(Math.round(v));
}

// The Signal IS the within-market percentile (round(100 × rank)), so we show it
// as an ordinal — "98th pct" — to read as a market rank, not an absolute verdict.
function ordinalSuffix(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
}

// Sort value per column + a null-last comparator (numbers numeric, text by
// locale). Shared by every Referrals header → click to sort a-z / z-a.
function refSortValue(
  id: ReferralsColumnId,
  r: OriginationSignalRow,
): number | string | null | undefined {
  switch (id) {
    case 'signal':
      return r.originationSignal;
    case 'sellerReadiness':
      return r.sellerReadiness;
    case 'entity':
      return r.name;
    case 'country':
      return r.country;
    case 'region':
      return r.region;
    case 'subsegment':
      return r.subsegment;
    case 'revenue':
      return r.revenueEur;
    case 'revConf':
      return r.revenueConfidence;
    case 'reason':
      return combineReasons(isProvenanceNote(r.note) ? humanModelReason(r) : r.note, r.sellerNote);
    case 'signalConf':
      return r.signalConfidence;
    case 'sellerConf':
      return r.sellerReadinessConfidence;
  }
}

function cmpRows(
  a: number | string | null | undefined,
  b: number | string | null | undefined,
  dir: 'asc' | 'desc',
): number {
  const an = a == null || a === '';
  const bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1; // nulls/blanks always sort last, regardless of direction
  if (bn) return -1;
  const r =
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
  return dir === 'asc' ? r : -r;
}

const REFERRAL_TOOLTIPS: Partial<Record<ReferralsColumnId, string>> = {
  signal:
    'Within-market percentile of our acquisition-attractiveness model. 100th = top of the market.',
  sellerReadiness:
    'Corryk Seller Readiness Score (0–100) — how gettable / under-pressure the owner is. UK-only for now; “—” where not yet computed.',
  signalConf:
    'Origination Signal data-completeness (owner age + filed accounts present = High). Not a measure of target quality.',
  sellerConf:
    'Seller Readiness Score data-completeness (how much seller-side evidence backs it). Indicative = thin data. Not a measure of how ready the owner is.',
};

export function OriginationSignalPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const ccy = useGlobalCurrency();
  const [countrySel, setCountrySel] = useState<Set<string>>(new Set());
  const [regionSel, setRegionSel] = useState<Set<string>>(new Set());
  const [subSel, setSubSel] = useState<Set<string>>(new Set());
  const [confSel, setConfSel] = useState<Set<string>>(new Set());
  const [revIdx, setRevIdx] = useState<[number, number]>([0, REV_LAST]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Stable per-mount seed → the server shuffles the above-bar set the same way
  // for every page, so Referrals reads as a random discovery sample (not a
  // top-scorer leaderboard) while paging stays consistent.
  const [seed] = useState(() => Math.random().toString(36).slice(2, 10));
  // Column layout (order / width / visibility), persisted per panel, shared by
  // the toolbar "Columns" manager and the table headers.
  const [cols, colActions] = useReferralsColumns('corryk:referrals-columns:v1');
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  // Client-side column sort (over the loaded rows). Null key = the default
  // randomised discovery order; clicking a header sorts a-z / z-a like Screener.
  const [sortKey, setSortKey] = useState<ReferralsColumnId | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Broadcast selection to the link channel (a linked Record follows). As a
  // discovery surface the highlight is local (broadcastOnly) — the list never
  // resets when a downstream panel broadcasts back.
  const selected = usePanelEntityId(tabId, panelId) ?? null;
  const select = useSetPanelEntity(tabId, panelId);

  // Effective countries (empty selection = all live markets).
  const effectiveCountries = countrySel.size
    ? ([...countrySel] as MarketCode[])
    : [...LIVE_COUNTRIES];

  // Regions are grouped under their country (GroupedRegionList) for the
  // effective countries — so with all countries selected the picker shows a
  // labelled group per country, like the Screener.
  const facets = useScreenerFacets();

  const countryOpts: SelectOption[] = LIVE_COUNTRIES.map((c) => ({
    value: c,
    label: MARKET_NAMES[c],
  }));
  const subOpts: SelectOption[] = CANONICAL_SUBSEGMENTS.map((s) => ({ value: s, label: s }));
  // Confidence = data-completeness of the score. Both shown by default; restrict
  // to High for fully-filed operators only. (Indicative never clears the bar.)
  const confOpts: SelectOption[] = [
    { value: 'High', label: 'High' },
    { value: 'Moderate', label: 'Moderate' },
  ];

  const revActive = revIdx[0] > 0 || revIdx[1] < REV_LAST;
  const activeCount =
    (countrySel.size ? 1 : 0) +
    (regionSel.size ? 1 : 0) +
    (subSel.size ? 1 : 0) +
    (confSel.size ? 1 : 0) +
    (revActive ? 1 : 0);

  const params = useMemo(
    () => ({
      country: effectiveCountries.join(','),
      subsegment: subSel.size ? [...subSel] : undefined,
      region: regionSel.size ? [...regionSel] : undefined,
      confidence: confSel.size ? [...confSel] : undefined,
      revenueMin: revIdx[0] > 0 ? REV_STOPS_M[revIdx[0]]! * 1_000_000 : undefined,
      revenueMax: revIdx[1] < REV_LAST ? REV_STOPS_M[revIdx[1]]! * 1_000_000 : undefined,
      seed,
    }),
    [effectiveCountries.join(','), subSel, regionSel, confSel, revIdx, seed],
  );

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useOriginationSignal(params);
  const rows = useMemo(() => (data?.pages ?? []).flatMap((p) => p.entities), [data]);

  function onSort(k: ReferralsColumnId) {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(REFERRALS_COLUMN_DEFS[k].numeric ? 'desc' : 'asc');
    }
  }
  const displayRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) =>
      cmpRows(refSortValue(sortKey, a), refSortValue(sortKey, b), sortDir),
    );
  }, [rows, sortKey, sortDir]);

  // Drill — double-click opens a floating Record pinned to the entity.
  const apiRef = useDockviewApi();
  const addPanel = useAppStore((s) => s.actions.addPanel);
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);
  function openEntity(entityId: string) {
    const api = apiRef.current;
    if (!api) return;
    const newPanelId = addPanel(tabId, { component: 'company-overview', title: 'Record' });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: newPanelId,
      component: 'company-overview' as PanelComponentKey,
      title: 'Record',
      params: { tabId, panelId: newPanelId },
      floating: { position: { left: 120 + offset, top: 90 + offset }, width: 760, height: 620 },
    });
    setPanelEntity(tabId, newPanelId, entityId);
  }

  // Open the Methodology panel deep-linked to the Origination Signal tab.
  function openMethodology() {
    const api = apiRef.current;
    if (!api) return;
    const newPanelId = addPanel(tabId, { component: 'methodology', title: 'Methodology' });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: newPanelId,
      component: 'methodology' as PanelComponentKey,
      title: 'Methodology',
      params: { tabId, panelId: newPanelId, initialState: { initialTab: 'signal' } },
      floating: { position: { left: 140 + offset, top: 90 + offset }, width: 720, height: 560 },
    });
  }

  function resetFilters() {
    setCountrySel(new Set());
    setRegionSel(new Set());
    setSubSel(new Set());
    setConfSel(new Set());
    setRevIdx([0, REV_LAST]);
  }
  const revLabel = (i: number) =>
    i <= 0 ? 'Any' : i >= REV_LAST ? 'No max' : fmtMoney(REV_STOPS_M[i]! * 1_000_000, ccy);

  // Visible columns in saved order → the grid template + its scroll width.
  const visibleCols = cols.columns.filter((c) => c.visible);
  const gridTemplate = visibleCols.map((c) => `${c.width}px`).join(' ');
  const gridMinWidth = visibleCols.reduce((sum, c) => sum + c.width, 0);
  const dim = <span className="os-dim">—</span>;

  function renderCell(
    id: ReferralsColumnId,
    r: OriginationSignalRow,
  ): { cls: string; node: React.ReactNode } {
    switch (id) {
      case 'signal': {
        if (r.originationSignal == null) return { cls: 'os-c os-sigcell', node: dim };
        const tt = clamp((r.originationSignal - 70) / 30, 0, 1);
        return {
          cls: 'os-c os-sigcell',
          node: (
            <>
              <span
                className="os-sig"
                style={{ background: ramp(tt), color: textOn(tt) }}
                title="Acquisition-attractiveness rank within its home market (percentile) — 100th = top of the market, not a certainty."
              >
                {r.originationSignal}
                {ordinalSuffix(r.originationSignal)}
              </span>
              <span className="os-sigsub">percentile</span>
            </>
          ),
        };
      }
      case 'sellerReadiness': {
        if (r.sellerReadiness == null) return { cls: 'os-c os-sigcell', node: dim };
        const tt = clamp((r.sellerReadiness - 40) / 50, 0, 1);
        return {
          cls: 'os-c os-sigcell',
          node: (
            <>
              <span
                className="os-sig"
                style={{ background: ramp(tt), color: textOn(tt) }}
                title="Corryk Seller Readiness Score (0–100) — how gettable / under-pressure the owner is."
              >
                {r.sellerReadiness}
              </span>
              <span className="os-sigsub">readiness</span>
            </>
          ),
        };
      }
      case 'entity':
        return { cls: 'os-c os-name', node: <span className="os-nm">{r.name ?? '—'}</span> };
      case 'country':
        return { cls: 'os-c', node: r.country };
      case 'region':
        return { cls: 'os-c', node: r.region ?? dim };
      case 'subsegment':
        return { cls: 'os-c os-sub', node: r.subsegment ?? dim };
      case 'revenue':
        return {
          cls: 'os-c os-r os-mono',
          node: r.revenueEur != null ? fmtMoney(r.revenueEur, ccy) : dim,
        };
      case 'revConf':
        return { cls: 'os-c os-r os-conf', node: r.revenueConfidence ?? dim };
      case 'reason': {
        const provenance = isProvenanceNote(r.note);
        const why = combineReasons(provenance ? humanModelReason(r) : r.note, r.sellerNote);
        return {
          cls: 'os-c os-reason',
          // The model-version audit trail stays reachable on hover.
          node: why ? (
            <span title={provenance ? (r.note ?? undefined) : undefined}>{why}</span>
          ) : (
            dim
          ),
        };
      }
      case 'signalConf':
        return { cls: 'os-c os-sigconf', node: r.signalConfidence ?? dim };
      case 'sellerConf':
        return { cls: 'os-c os-sigconf', node: r.sellerReadinessConfidence ?? dim };
    }
  }

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="os-root">
        <style>{OS_CSS}</style>

        <div className="os-toolbar">
          <button type="button" className="os-filterbtn" onClick={() => setFiltersOpen(true)}>
            <Filter size={12} /> Filters
            {activeCount > 0 && <span className="os-fbadge">{activeCount}</span>}
          </button>
          <button
            ref={columnsBtnRef}
            type="button"
            className="os-filterbtn"
            aria-haspopup="menu"
            aria-expanded={columnsOpen}
            title="Show, hide and reorder columns"
            onClick={() => setColumnsOpen((v) => !v)}
          >
            <Columns3 size={12} /> Columns
          </button>
          <ColumnsMenu
            open={columnsOpen}
            onClose={() => setColumnsOpen(false)}
            anchorRef={columnsBtnRef}
            columns={cols.columns}
            label={(id) => REFERRALS_COLUMN_DEFS[id].label}
            actions={colActions}
          />
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="os-filterbtn"
            title="How the Corryk Origination Signal is built"
            onClick={openMethodology}
          >
            <BookOpen size={12} /> Methodology
          </button>
        </div>

        <div className="os-listwrap">
          {isError ? (
            <div className="os-empty">Couldn’t load referrals.</div>
          ) : isLoading ? (
            <div className="os-empty">Loading…</div>
          ) : rows.length === 0 ? (
            <PanelEmptyState fill icon={<Radar size={18} />} title="No targets in range">
              Nothing matches these filters yet. Ease the sub-segment, region or signal thresholds —
              the highest-scoring acquisition targets surface here, ranked by Corryk Origination
              Signal.
            </PanelEmptyState>
          ) : (
            <>
              <div className="os-grid" role="table" style={{ minWidth: gridMinWidth }}>
                <div className="os-head" role="row" style={{ gridTemplateColumns: gridTemplate }}>
                  {visibleCols.map((c) => (
                    <ColumnHeaderCell
                      key={c.id}
                      col={c}
                      columns={cols.columns}
                      label={REFERRALS_COLUMN_DEFS[c.id].label}
                      numeric={REFERRALS_COLUMN_DEFS[c.id].numeric}
                      active={sortKey === c.id}
                      sortDir={sortDir}
                      onSort={onSort}
                      moveColumn={colActions.moveColumn}
                      setColumnWidth={colActions.setColumnWidth}
                      tooltip={REFERRAL_TOOLTIPS[c.id]}
                    />
                  ))}
                </div>
                {displayRows.map((r) => (
                  <EntityRowContextMenu key={r.entityId} entityId={r.entityId} legalName={r.name}>
                    <div
                      role="row"
                      className={'os-row' + (selected === r.entityId ? ' sel' : '')}
                      style={{ gridTemplateColumns: gridTemplate }}
                      onClick={() => select(r.entityId)}
                      onDoubleClick={() => openEntity(r.entityId)}
                    >
                      {visibleCols.map((c) => {
                        const { cls, node } = renderCell(c.id, r);
                        return (
                          <div key={c.id} className={cls}>
                            {node}
                          </div>
                        );
                      })}
                    </div>
                  </EntityRowContextMenu>
                ))}
              </div>
              {hasNextPage && (
                <button
                  type="button"
                  className="os-more"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              )}
            </>
          )}
        </div>

        <FilterModalShell
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          title="Filters"
          resetLabel="Reset"
          doneLabel="Done"
          onReset={resetFilters}
        >
          <CollapsibleFilterRow
            label="Country"
            summary={summarizeSelection(countrySel, (v) => MARKET_NAMES[v as MarketCode] ?? v)}
            active={countrySel.size > 0}
          >
            <MultiSelectList options={countryOpts} selected={countrySel} onChange={setCountrySel} />
          </CollapsibleFilterRow>
          <CollapsibleFilterRow
            label="Region"
            summary={summarizeSelection(regionSel, (v) => v)}
            active={regionSel.size > 0}
          >
            <GroupedRegionList
              regionsByCountry={facets.data?.regionsByCountry ?? {}}
              countries={effectiveCountries}
              selected={regionSel}
              onChange={setRegionSel}
            />
          </CollapsibleFilterRow>
          <CollapsibleFilterRow
            label="Sub-segment"
            summary={summarizeSelection(subSel, (v) => v)}
            active={subSel.size > 0}
          >
            <MultiSelectList options={subOpts} selected={subSel} onChange={setSubSel} />
          </CollapsibleFilterRow>
          <CollapsibleFilterRow
            label="Revenue estimate"
            summary={revActive ? `${revLabel(revIdx[0])} – ${revLabel(revIdx[1])}` : 'All'}
            active={revActive}
          >
            <div className="os-slider-wrap">
              <Slider.Root
                className="os-slider"
                value={revIdx}
                min={0}
                max={REV_LAST}
                step={1}
                onValueChange={(v) => setRevIdx([v[0]!, v[1]!])}
              >
                <Slider.Track className="os-slider-track">
                  <Slider.Range className="os-slider-range" />
                </Slider.Track>
                <Slider.Thumb className="os-slider-thumb" aria-label="Min revenue" />
                <Slider.Thumb className="os-slider-thumb" aria-label="Max revenue" />
              </Slider.Root>
              <div className="os-slider-labels">
                <span>{revLabel(revIdx[0])}</span>
                <span>{revLabel(revIdx[1])}</span>
              </div>
            </div>
          </CollapsibleFilterRow>
          <CollapsibleFilterRow
            label="Signal confidence"
            summary={confSel.size ? summarizeSelection(confSel, (v) => v) : 'All'}
            active={confSel.size > 0}
          >
            <MultiSelectList options={confOpts} selected={confSel} onChange={setConfSel} />
          </CollapsibleFilterRow>
        </FilterModalShell>
      </div>
    </PanelFrame>
  );
}

const OS_CSS = `
.os-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); font-variant-numeric:tabular-nums; }
.os-toolbar { display:flex; align-items:center; gap:var(--s-3); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); }
.os-filterbtn { display:inline-flex; align-items:center; gap:4px; font:inherit; font-size:var(--t-body); font-weight:400; color:var(--text-primary); background:var(--bg); border:1px solid var(--hairline); border-radius:2px; padding:2px 8px; cursor:pointer; }
.os-filterbtn:hover { background:var(--hover-subtle); }
.os-fbadge { display:inline-flex; align-items:center; justify-content:center; min-width:16px; height:16px; padding:0 4px; border-radius:8px; background:var(--accent); color:var(--bg); font-size:10px; font-weight:700; }
/* The table block is inset from the panel edges (matching the Screener/Triage
   tables, whose body container is padded) so the header rule, row borders and
   row backgrounds sit INSIDE the panel rather than bleeding to its chrome. */
.os-listwrap { flex:1; overflow:auto; min-height:0; padding:0 var(--s-3); margin-top:var(--s-4); }
.os-empty { padding:var(--s-4); color:var(--text-tertiary); }
/* Div-grid (not a <table>) mirroring the Screener/Watchlist tables: 22px header
   + 2px rule on --surface-elevated, --s-2 cell inset. All data cells use
   --t-body to match the other panels; only the small Signal sub-caption is
   --t-meta. Column template + scroll width come from the per-panel column
   config (inline grid-template-columns), so columns reorder / resize / hide. */
.os-grid { width:100%; }
.os-head { position:sticky; top:0; z-index:1; display:grid; height:22px; background:var(--surface-elevated); border-bottom:2px solid var(--text-primary); }
.os-row { display:grid; align-items:start; border-bottom:1px solid var(--border-subtle); cursor:pointer; font-size:var(--t-body); }
.os-row:hover { background:var(--hover-subtle); }
.os-row.sel { background:var(--color-accent-100); }
.os-c { padding:6px var(--s-2); min-width:0; color:var(--text-primary); font-size:var(--t-body); }
.os-c:not(.os-reason):not(.os-sigcell) { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.os-c.os-r { text-align:right; }
/* Signal = the within-market percentile, an ordinal chip + a muted "pct" unit on
   one line, so a high score reads as a market rank. Its data-completeness is its
   own "Signal confidence" column now, not stacked here. */
.os-sigcell { display:flex; flex-direction:column; align-items:flex-start; }
.os-sig { display:inline-flex; align-items:center; justify-content:center; min-width:30px; height:18px; padding:0 5px; border-radius:3px; font-size:var(--t-body); font-weight:700; letter-spacing:-0.01em; }
.os-sigsub { margin-top:2px; font-size:var(--t-meta); color:var(--text-tertiary); }
.os-nm { font-weight:600; }
.os-sub { color:var(--text-secondary); }
.os-mono { font-family:var(--font-mono, monospace); font-weight:600; }
.os-conf { font-family:var(--font-mono, monospace); color:var(--text-secondary); }
.os-sigconf { color:var(--text-secondary); }
.os-reason { white-space:normal; color:var(--text-secondary); line-height:1.35; }
.os-dim { color:var(--text-tertiary); }
/* Small, standard-style button (matches the Filters button) left-aligned to the
   first column — its text inset (8px padding) sits on the same vertical guide as
   the first column's cell text (--s-2 inset). */
.os-more { display:block; width:fit-content; margin:var(--s-3) 0; font:inherit; font-size:var(--t-body); font-weight:400; color:var(--text-primary); background:var(--bg); border:1px solid var(--hairline); border-radius:2px; padding:2px 8px; cursor:pointer; }
.os-more:hover:not(:disabled) { background:var(--hover-subtle); }
.os-slider-wrap { padding:8px 6px 2px; }
.os-slider { position:relative; display:flex; align-items:center; width:100%; height:18px; user-select:none; touch-action:none; }
.os-slider-track { position:relative; flex:1; height:4px; border-radius:2px; background:var(--hairline); }
.os-slider-range { position:absolute; height:100%; border-radius:2px; background:var(--accent); }
.os-slider-thumb { display:block; width:14px; height:14px; border-radius:50%; background:var(--surface); border:2px solid var(--accent); cursor:grab; }
.os-slider-thumb:active { cursor:grabbing; }
.os-slider-labels { display:flex; justify-content:space-between; margin-top:6px; font-size:var(--t-meta); color:var(--text-secondary); font-weight:500; }
`;
